/**
 * Busca por nome de grupo.
 *
 * Os nomes reais do WhatsApp do dono são assim:
 *
 *   `#Fábio Furtado - Comunidades`
 *   `[M1/12] SDS.ADTN - Projeto Energia`
 *   `#1 Comunidade 3D com Lucas`
 *
 * Acento, `#`, colchete, ponto e barra. Duas armadilhas já pagas em outro projeto e que
 * este arquivo existe para não repetir:
 *
 *  - **`nome.includes(consulta)` quebra com a ordem trocada.** Quem lembra "energia" e
 *    "projeto" digita `energia projeto`, e o nome tem `Projeto Energia`. Um `includes` da
 *    frase inteira não acha, e a pessoa conclui que o grupo não está na lista.
 *  - **Acento não pode decidir.** Digitar `fabio` tem que achar `Fábio`, e `FÁBIO` tem que
 *    achar `fabio`.
 *
 * O jeito: os dois lados passam pela MESMA normalização (sem acento, minúsculo, pontuação
 * virando espaço) e a consulta é quebrada em termos. Todos os termos precisam aparecer,
 * em qualquer ordem e em qualquer ponto da palavra — `unidade` acha `Comunidades`.
 */

/**
 * Tira acento, baixa a caixa e transforma pontuação em espaço.
 *
 * A pontuação virar espaço é o que faz `SDS.ADTN` casar tanto com `sds.adtn` quanto com
 * `sds adtn`: quem digita raramente reproduz o `#`, o `[` e a `/` do nome original.
 */
export function normalizeForSearch(value: string): string {
  return (
    value
      .normalize('NFD')
      // Remove só as marcas de combinação que o NFD separou — é o que apaga o acento sem
      // tocar na letra. `\p{M}` exige a flag `u`.
      .replace(/\p{M}+/gu, '')
      .toLowerCase()
      // Tudo que não for letra ou número vira espaço: `#`, `[`, `]`, `/`, `.`, `-`, `—`.
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim()
  );
}

/** Os termos da consulta, já normalizados. Consulta em branco devolve lista vazia. */
export function searchTerms(query: string): string[] {
  const normalized = normalizeForSearch(query);
  return normalized === '' ? [] : normalized.split(' ');
}

/**
 * O grupo casa com a consulta?
 *
 * Consulta vazia casa com todo mundo (é o estado da lista sem filtro). `name` nulo não é
 * derrota automática: o JID entra no palheiro, então dá para achar pelo id um grupo cujo
 * nome o WhatsApp não mandou.
 */
export function matchesSearch(
  group: { name: string | null; providerGroupId: string },
  query: string,
): boolean {
  const terms = searchTerms(query);
  if (terms.length === 0) return true;

  const haystack = normalizeForSearch(`${group.name ?? ''} ${group.providerGroupId}`);
  // `every`: todos os termos, ordem livre. É o que separa esta busca de um `includes`.
  return terms.every((term) => haystack.includes(term));
}
